import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { hubSpotSyncService, type SyncOptions } from '@/services/hubspot/syncService';
import { toast } from 'sonner';

// Hook for HubSpot configuration
export function useHubSpotConfig() {
  return useQuery({
    queryKey: ['hubspot-config'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hubspot_config')
        .select('*')
        .single();

      if (error && error.code !== 'PGRST116') {
        throw error;
      }

      return data;
    },
  });
}

// Hook for HubSpot sync statistics
export function useHubSpotStats() {
  return useQuery({
    queryKey: ['hubspot-stats'],
    queryFn: async () => {
      try {
      // Get sync counts
      const [contacts, companies, deals] = await Promise.all([
        supabase.from('contacts').select('id', { count: 'exact', head: true }).not('hubspot_id', 'is', null),
        supabase.from('organizations').select('id', { count: 'exact', head: true }).not('hubspot_id', 'is', null),
        supabase.from('opportunities').select('id', { count: 'exact', head: true }).not('hubspot_deal_id', 'is', null),
      ]);

      // Get last sync info
      const { data: lastSyncData } = await supabase
        .from('hubspot_sync_history')
        .select('*')
        .eq('status', 'completed')
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false })
        .limit(1);
      
      const lastSync = lastSyncData?.[0] || null;

      // Get pending conflicts count
      const { count: pendingConflicts } = await supabase
        .from('hubspot_sync_conflicts')
        .select('id', { count: 'exact', head: true })
        .eq('resolution_status', 'pending');

      // Get recent activity
      const { data: recentActivity } = await supabase
        .from('hubspot_sync_history')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(5);

      return {
        contacts: contacts.count || 0,
        companies: companies.count || 0,
        deals: deals.count || 0,
        lastSync: lastSync?.completed_at,
        nextSync: null, // Calculate based on schedule
        pendingConflicts: pendingConflicts || 0,
        recentActivity: recentActivity?.map(activity => ({
          description: `${activity.entity_type} sync - ${activity.records_processed} records`,
          timestamp: activity.started_at,
          status: activity.status === 'completed' ? 'success' : 'error',
        })) || [],
      };
      } catch (error) {
        throw error;
      }
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
}

// Hook for triggering HubSpot sync
export function useHubSpotSync() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (options: SyncOptions) => {
      await hubSpotSyncService.initialize();
      return hubSpotSyncService.startSync(options);
    },
    onSuccess: (syncId) => {
      toast.success('HubSpot sync started successfully');
      queryClient.invalidateQueries({ queryKey: ['hubspot-stats'] });
      queryClient.invalidateQueries({ queryKey: ['hubspot-sync-history'] });
      
      // Poll for sync status
      const checkStatus = setInterval(async () => {
        const status = await hubSpotSyncService.getSyncStatus(syncId);
        if (status?.status === 'completed') {
          clearInterval(checkStatus);
          toast.success('HubSpot sync completed successfully');
          queryClient.invalidateQueries();
        } else if (status?.status === 'failed') {
          clearInterval(checkStatus);
          toast.error('HubSpot sync failed');
          queryClient.invalidateQueries();
        }
      }, 2000);
    },
    onError: (error) => {
      toast.error(`Sync failed: ${error.message}`);
    },
  });
}

// Hook for sync history
export function useHubSpotSyncHistory() {
  return useQuery({
    queryKey: ['hubspot-sync-history'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('hubspot_sync_history')
        .select('*')
        .order('started_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data;
    },
  });
}

// Hook for field mappings
export function useHubSpotFieldMappings(entityType?: string) {
  return useQuery({
    queryKey: ['hubspot-field-mappings', entityType],
    queryFn: async () => {
      let query = supabase
        .from('hubspot_field_mappings')
        .select('*')
        .eq('is_active', true);

      if (entityType) {
        query = query.eq('entity_type', entityType);
      }

      const { data, error } = await query.order('created_at');

      if (error) throw error;
      return data;
    },
  });
}

// Hook for saving field mappings
export function useSaveFieldMapping() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (mapping: any) => {
      const { data, error } = await supabase
        .from('hubspot_field_mappings')
        .upsert(mapping, {
          onConflict: 'entity_type,hubspot_field,local_field',
        });

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Field mapping saved successfully');
      queryClient.invalidateQueries({ queryKey: ['hubspot-field-mappings'] });
    },
    onError: (error) => {
      toast.error(`Failed to save mapping: ${error.message}`);
    },
  });
}

// Hook for sync conflicts
export function useHubSpotConflicts(status?: string) {
  return useQuery({
    queryKey: ['hubspot-conflicts', status],
    queryFn: async () => {
      let query = supabase
        .from('hubspot_sync_conflicts')
        .select('*');

      if (status) {
        query = query.eq('resolution_status', status);
      }

      const { data, error } = await query.order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
  });
}

// Hook for resolving conflicts
export function useResolveConflict() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ conflictId, resolution }: { conflictId: string; resolution: any }) => {
      const { data, error } = await supabase
        .from('hubspot_sync_conflicts')
        .update({
          resolution_status: 'resolved',
          resolution_action: resolution.action,
          resolution_notes: resolution.notes,
          resolved_at: new Date().toISOString(),
          resolved_by: (await supabase.auth.getUser()).data.user?.id,
        })
        .eq('id', conflictId);

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast.success('Conflict resolved successfully');
      queryClient.invalidateQueries({ queryKey: ['hubspot-conflicts'] });
      queryClient.invalidateQueries({ queryKey: ['hubspot-stats'] });
    },
    onError: (error) => {
      toast.error(`Failed to resolve conflict: ${error.message}`);
    },
  });
}

// Hook for saving HubSpot configuration
export function useSaveHubSpotConfig() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (config: any) => {
      const { data: existing } = await supabase
        .from('hubspot_config')
        .select('id')
        .single();

      if (existing) {
        // Update existing config
        const { data, error } = await supabase
          .from('hubspot_config')
          .update({
            ...config,
            updated_at: new Date().toISOString(),
          })
          .eq('id', existing.id);

        if (error) throw error;
        return data;
      } else {
        // Create new config
        const { data, error } = await supabase
          .from('hubspot_config')
          .insert(config);

        if (error) throw error;
        return data;
      }
    },
    onSuccess: () => {
      toast.success('HubSpot configuration saved successfully');
      queryClient.invalidateQueries({ queryKey: ['hubspot-config'] });
    },
    onError: (error) => {
      toast.error(`Failed to save configuration: ${error.message}`);
    },
  });
}